import React, {ReactElement, useEffect, useRef, useState} from 'react';
import Accordion from '@mui/material/Accordion';
import Alert from '@mui/material/Alert';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select, {SelectChangeEvent} from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {Dayjs} from 'dayjs';
import {toast} from 'react-toastify';
import {uuid} from '../../../../utils/utils';
import IRPService from '../../../../services/irp.service';
import newOccurrenceService from '../../../../services/new-occurrence.service';
import OccurrenceReportDescriptorService from '../../../../services/occurrence-report-descriptor.service';
import OccurrenceReportDescriptor, {
    OccurrenceReportDescriptorTerm
} from '../../../../@types/occurrence-report-descriptor';
import {IRPReportSearchCriteria} from '../../../../@types/integrated-risk-picture';
import {
    ShakedownReportRow,
    ShakedownRequest,
    ShakedownResult,
    ShakedownSnapshot,
} from '../../../../@types/model-quality';
import {getModelTestingCache} from './modelTestingCache';
import EventSequence from '../../../../@types/event-sequence';
import {FaultTreeDendrogramView}
    from '../../tabeditor/editors/irp-workbook/components/FaultTreeDendrogram';
import {
    TestingScope,
    groupIdForScope,
    modelForScope,
    scopeKey,
    shakedownSnapshotForScope,
    startShakedownForScope,
} from './testing-scope';

type Phase = 'input' | 'running' | 'result' | 'error';

const POLL_INTERVAL_MS = 2000;
const MAX_CONSECUTIVE_POLL_ERRORS = 3;
const MAX_REPORTS = 50;
const DEFAULT_LIMIT = 25;

interface ReportSource {
    sourceName: string;
    reportCount: number;
}

/** Plain-language explanations of the resolution-log rejection categories. */
const REJECTION_HELP: Record<string, string> = {
    'Hallucinated nominations':
        'The AI nominated a node that does not exist in this model. This is caught instantly by a deterministic '
        + 'check at no cost. It is most common on reports that do not fit the model at all, where the AI strains to '
        + 'suggest something instead of saying "no match". No action needed unless a report you expected to map came '
        + 'out not applicable.',
    'Observation call failures':
        'An AI call failed even after retries, so this candidate was skipped. If the same report shows several of '
        + 'these, check the backend logs.',
    'Rejected by observation':
        'The AI examined the model section around the candidate and concluded that none of the states there actually '
        + 'occurred in the report. This is the intended way reports outside the model\'s scope get filtered out.',
    'Rejected by verification':
        'The candidate looked plausible, but a focused check of its statement of truth against the report failed, so '
        + 'it was discarded. Recurring failures on the same statement suggest the wording asks for more than reports '
        + 'can say (see Model signals).',
    'Other rejections':
        'Rejections that do not fit the categories above; read the individual reasons.',
};

/** Plain-language explanations of the integrity-finding categories. */
const INTEGRITY_HELP: Record<string, string> = {
    'Verification disagreements':
        'The gate arithmetic suggested one entry state, but a focused check of that state\'s own statement against '
        + 'the report disagreed. The mapping fell back to the state the AI directly observed and was flagged instead '
        + 'of guessing. This usually points at a statement whose wording demands more than the report can say; the '
        + 'quoted reason names the gap.',
    'Resolution did not stabilize':
        'The entry point kept moving between candidate states, so the algorithm stopped after its re-anchor limit '
        + 'and accepted the deeper state for human review.',
    'Contradictory observations':
        'A control was judged successful, but the state it should have prevented still verified as having happened. '
        + 'The report may describe multiple attempts or events; review which one the mapping should reflect.',
};

/**
 * Model shakedown panel, hosted as the "Shakedown" tab of the Model Testing modal:
 * batch test-mapping over a report set selected with the same criteria filter as
 * IRP creation (sources, descriptor terms, date range, limit, live count), with an
 * advanced option for an explicit report ID list. Traces are aggregated into a
 * model quality report; nothing is persisted. Polling stops on unmount (the modal
 * unmounts its panels when closed).
 */
export default function ModelShakedownPanel(
    props: { scope: TestingScope | null }): ReactElement {
    const {scope} = props;
    const cacheKey = scope ? scopeKey(scope) : null;
    const [phase, setPhase] = useState<Phase>('input');
    const [groupId, setGroupId] = useState<number | undefined>(undefined);
    const [reportSources, setReportSources] = useState<ReportSource[]>([]);
    const [selectedSources, setSelectedSources] = useState<string[]>([]);
    const [startDate, setStartDate] = useState<Dayjs | null>(null);
    const [endDate, setEndDate] = useState<Dayjs | null>(null);
    const [limit, setLimit] = useState<number>(DEFAULT_LIMIT);
    const [publishedDescriptors, setPublishedDescriptors] = useState<OccurrenceReportDescriptor[]>([]);
    const [selectedDescriptorId, setSelectedDescriptorId] = useState<string>('');
    const [selectedDescriptorTermIds, setSelectedDescriptorTermIds] = useState<string[]>([]);
    const [previewCount, setPreviewCount] = useState<number | null>(null);
    const [useExplicitIds, setUseExplicitIds] = useState<boolean>(false);
    const [reportIdText, setReportIdText] = useState<string>('');
    const [snapshot, setSnapshot] = useState<ShakedownSnapshot | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [resultTab, setResultTab] = useState<number>(0);
    const [model, setModel] = useState<EventSequence | null>(null);
    // The dendrogram's collapse and pan state. Held in refs, not state, because the drawing effect
    // reads them without wanting to redraw when they change: the workbook keeps them in its store
    // for the same reason, and a shakedown has no store to keep them in.
    const collapsedNodeIdsRef = useRef<Set<number> | null>(null);
    const transformRef = useRef<{x: number; y: number; k: number} | null>(null);
    const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const activeRunIdRef = useRef<string | null>(null);

    const stopPolling = (): void => {
        if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
        }
        activeRunIdRef.current = null;
    };

    useEffect((): (() => void) => stopPolling, []);

    // Restore the most recent shakedown for this model (finished results reappear,
    // an in-flight run resumes polling); "New Run" clears it.
    useEffect((): void => {
        if (scope == null || cacheKey == null) {
            return;
        }
        const cached = getModelTestingCache(cacheKey).shakedown;
        if (!cached) {
            return;
        }
        setSnapshot(cached.snapshot);
        setErrorMessage(cached.errorMessage);
        if (cached.errorMessage != null) {
            setPhase('error');
        } else if (cached.snapshot?.result != null) {
            setPhase('result');
        } else {
            setPhase('running');
            activeRunIdRef.current = cached.runId;
            void poll(scope, cached.runId, 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cacheKey]);

    // On mount: resolve the event sequence's group and load its report sources
    // (the same source list the IRP creation filter offers).
    useEffect((): void => {
        if (scope == null) {
            return;
        }
        modelForScope(scope)
            .then(setModel)
            .catch(() => setModel(null));
        groupIdForScope(scope)
            .then((resolvedGroupId: number | undefined) => {
                setGroupId(resolvedGroupId);
                return newOccurrenceService.getReportSources(resolvedGroupId);
            })
            .then((sources: ReportSource[]) => {
                setReportSources(sources);
                setSelectedSources(sources.map(s => s.sourceName));
            })
            .catch(() => {
                setReportSources([]);
                setSelectedSources([]);
            });
        // Same published descriptor list the IRP creation filter offers
        OccurrenceReportDescriptorService
            .fetchPublishedDescriptors({size: 10000, offset: 0, sort: 'name', direction: 'asc'})
            .then(page => setPublishedDescriptors(page.content || []))
            .catch(() => setPublishedDescriptors([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cacheKey]);

    const selectedDescriptor = publishedDescriptors.find(d => d.id.toString() === selectedDescriptorId);
    const descriptorTerms: OccurrenceReportDescriptorTerm[] = selectedDescriptor?.descriptorTerms ?? [];

    /** Term name lookup across all published descriptors (selections span descriptors). */
    const termNameById = (termId: string): string => {
        for (const descriptor of publishedDescriptors) {
            const term = descriptor.descriptorTerms?.find(t => t.id?.toString() === termId);
            if (term) {
                return term.term;
            }
        }
        return termId;
    };

    const buildCriteria = (): IRPReportSearchCriteria => ({
        includeAllReports: false,
        sources: selectedSources,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        descriptorTermIds: selectedDescriptorTermIds.length > 0
            ? selectedDescriptorTermIds.map(id => parseInt(id, 10))
            : undefined,
        limit: Math.min(limit, MAX_REPORTS),
        groupId: groupId,
    });

    // Live report count preview (debounced), same behavior as the IRP creation filter
    useEffect((): (() => void) | void => {
        if (useExplicitIds || selectedSources.length === 0) {
            setPreviewCount(null);
            return;
        }
        const timeoutId = setTimeout(async (): Promise<void> => {
            try {
                const count = await IRPService.previewReportCount({
                    ...buildCriteria(),
                    limit: undefined,
                });
                setPreviewCount(count);
            } catch {
                setPreviewCount(null);
            }
        }, 400);
        return (): void => clearTimeout(timeoutId);
    }, [useExplicitIds, selectedSources, startDate, endDate, groupId, selectedDescriptorTermIds]);

    const parseReportIds = (): string[] => {
        return Array.from(new Set(reportIdText
            .split(/[\n,;\s]+/)
            .map(id => id.trim())
            .filter(id => id.length > 0)));
    };

    const poll = async (target: TestingScope, theRunId: string, consecutiveErrors: number): Promise<void> => {
        if (activeRunIdRef.current !== theRunId) {
            return;
        }
        try {
            const snap = await shakedownSnapshotForScope(target, theRunId);
            if (activeRunIdRef.current !== theRunId) {
                return;
            }
            setSnapshot(snap);
            getModelTestingCache(scopeKey(target)).shakedown =
                {runId: theRunId, snapshot: snap, errorMessage: snap.error ?? null};
            if (snap.error) {
                setErrorMessage(snap.error);
                setPhase('error');
                stopPolling();
                return;
            }
            if (snap.done && snap.result) {
                setPhase('result');
                stopPolling();
                return;
            }
            pollTimerRef.current = setTimeout(() => { void poll(target, theRunId, 0); }, POLL_INTERVAL_MS);
        } catch (error) {
            if (activeRunIdRef.current !== theRunId) {
                return;
            }
            if (consecutiveErrors + 1 >= MAX_CONSECUTIVE_POLL_ERRORS) {
                setErrorMessage(error instanceof Error ? error.message : 'Shakedown polling failed');
                setPhase('error');
                stopPolling();
                return;
            }
            pollTimerRef.current = setTimeout(
                () => { void poll(target, theRunId, consecutiveErrors + 1); }, POLL_INTERVAL_MS);
        }
    };

    const start = (): void => {
        if (scope == null || cacheKey == null) {
            return;
        }
        let request: ShakedownRequest;
        if (useExplicitIds) {
            const reportIds = parseReportIds();
            if (reportIds.length === 0) {
                toast.warning('Enter at least one report ID');
                return;
            }
            if (reportIds.length > MAX_REPORTS) {
                toast.warning(`At most ${MAX_REPORTS} reports per shakedown (you entered ${reportIds.length})`);
                return;
            }
            request = {runId: uuid(), reportIds};
        } else {
            if (selectedSources.length === 0) {
                toast.warning('Select at least one report source');
                return;
            }
            request = {runId: uuid(), criteria: buildCriteria()};
        }
        activeRunIdRef.current = request.runId;
        setPhase('running');
        setSnapshot(null);
        setResultTab(0);
        getModelTestingCache(cacheKey).shakedown = {runId: request.runId, snapshot: null, errorMessage: null};
        startShakedownForScope(scope, request)
            .then(() => { void poll(scope, request.runId, 0); })
            .catch((error: Error) => {
                setErrorMessage(error.message);
                getModelTestingCache(cacheKey).shakedown = {
                    runId: request.runId, snapshot: null, errorMessage: error.message,
                };
                setPhase('error');
                stopPolling();
            });
    };

    const toggleSource = (sourceName: string): void => {
        setSelectedSources(prev => prev.includes(sourceName)
            ? prev.filter(s => s !== sourceName)
            : [...prev, sourceName]);
    };

    const renderInput = (): ReactElement => (
        <Stack spacing={2}>
            <Typography variant="body2">
                Maps a sample of reports against this model and aggregates the results into a
                model quality report. Nothing is saved. Up to {MAX_REPORTS} reports per run.
            </Typography>

            <Box>
                <Typography variant="subtitle2" sx={{mb: 0.5}}>Report sources</Typography>
                <FormGroup row>
                    {reportSources.map((source: ReportSource): ReactElement => (
                        <FormControlLabel
                            key={source.sourceName}
                            control={<Checkbox
                                checked={selectedSources.includes(source.sourceName)}
                                onChange={(): void => toggleSource(source.sourceName)}
                                disabled={useExplicitIds}
                            />}
                            label={`${source.sourceName} (${source.reportCount})`}
                        />
                    ))}
                    {reportSources.length === 0 && (
                        <Typography variant="caption" color="text.secondary">
                            No report sources available for this model's group.
                        </Typography>
                    )}
                </FormGroup>
            </Box>

            <Box>
                <Typography variant="subtitle2" sx={{mb: 0.5}}>Filter by Descriptor Terms (Optional)</Typography>
                {selectedDescriptorTermIds.length > 0 && (
                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1}}>
                        {selectedDescriptorTermIds.map((termId: string): ReactElement => (
                            <Tooltip key={termId} title={termNameById(termId)} placement="top">
                                <Chip
                                    label={termNameById(termId)}
                                    size="small"
                                    color="primary"
                                    onDelete={(): void => setSelectedDescriptorTermIds(
                                        prev => prev.filter(id => id !== termId))}
                                />
                            </Tooltip>
                        ))}
                    </Box>
                )}
                <Stack direction="row" spacing={2}>
                    <FormControl size="small" sx={{minWidth: 260}} disabled={useExplicitIds}>
                        <InputLabel>Descriptors</InputLabel>
                        <Select
                            value={selectedDescriptorId}
                            onChange={(event: SelectChangeEvent<string>): void =>
                                setSelectedDescriptorId(event.target.value)}
                            label="Descriptors"
                        >
                            <MenuItem value="">
                                <em>Select a descriptor</em>
                            </MenuItem>
                            {publishedDescriptors.map((descriptor: OccurrenceReportDescriptor): ReactElement => (
                                <MenuItem key={descriptor.id} value={descriptor.id.toString()}>
                                    {descriptor.name}{descriptor.version ? ` (v.${descriptor.version})` : ''}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{minWidth: 300, flexGrow: 1}}
                        disabled={useExplicitIds || !selectedDescriptorId}>
                        <InputLabel>Descriptor Terms</InputLabel>
                        <Select
                            multiple
                            value={selectedDescriptorTermIds}
                            onChange={(event: SelectChangeEvent<string[]>): void => {
                                const value = event.target.value;
                                setSelectedDescriptorTermIds(
                                    typeof value === 'string' ? value.split(',') : value);
                            }}
                            label="Descriptor Terms"
                            renderValue={(selected: string[]): ReactElement => (
                                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                                    {selected.map((id: string): ReactElement => (
                                        <Chip key={id} label={termNameById(id)} size="small" />
                                    ))}
                                </Box>
                            )}
                        >
                            {descriptorTerms.map((term: OccurrenceReportDescriptorTerm): ReactElement => (
                                <MenuItem key={term.id} value={term.id!.toString()}>
                                    <Checkbox checked={selectedDescriptorTermIds.includes(term.id!.toString())} />
                                    <ListItemText primary={term.term} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            </Box>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Stack direction="row" spacing={2}>
                    <DatePicker
                        label="From"
                        value={startDate}
                        onChange={(value: Dayjs | null): void => setStartDate(value)}
                        disabled={useExplicitIds}
                        slotProps={{textField: {size: 'small'}}}
                    />
                    <DatePicker
                        label="To"
                        value={endDate}
                        onChange={(value: Dayjs | null): void => setEndDate(value)}
                        disabled={useExplicitIds}
                        slotProps={{textField: {size: 'small'}}}
                    />
                    <TextField
                        label="Report limit"
                        type="number"
                        size="small"
                        value={limit}
                        onChange={(event): void => {
                            const parsed = parseInt(event.target.value, 10);
                            setLimit(Number.isNaN(parsed) ? DEFAULT_LIMIT : Math.max(1, Math.min(parsed, MAX_REPORTS)));
                        }}
                        disabled={useExplicitIds}
                        sx={{width: 130}}
                    />
                </Stack>
            </LocalizationProvider>

            {!useExplicitIds && (
                <Typography variant="caption" color="text.secondary">
                    {previewCount != null
                        ? `${previewCount} report(s) match the criteria; the shakedown will map ${Math.min(previewCount, limit)} of them.`
                        : 'Matching report count will appear here.'}
                </Typography>
            )}

            <Accordion disableGutters expanded={useExplicitIds}
                onChange={(_, expanded: boolean): void => setUseExplicitIds(expanded)}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2">Advanced: specify report IDs explicitly</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <TextField
                        label="Report IDs (one per line or comma-separated)"
                        multiline
                        fullWidth
                        minRows={5}
                        value={reportIdText}
                        onChange={(event): void => setReportIdText(event.target.value)}
                        placeholder={'DFW08LA089\nCEN22LA334\n...'}
                    />
                    <Typography variant="caption" color="text.secondary">
                        {parseReportIds().length} report(s) entered
                    </Typography>
                </AccordionDetails>
            </Accordion>
        </Stack>
    );

    const renderRows = (rows: ShakedownReportRow[]): ReactElement => (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>Report</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Entry Point</TableCell>
                    <TableCell align="right">Mappings</TableCell>
                    <TableCell align="right">Time</TableCell>
                    <TableCell>Notes</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((row: ShakedownReportRow): ReactElement => (
                    <TableRow key={row.reportId}>
                        <TableCell>{row.reportId}</TableCell>
                        <TableCell>{row.running ? 'RUNNING' : row.error ? 'ERROR' : row.status ?? ''}</TableCell>
                        <TableCell>{row.entryNodeName ?? ''}</TableCell>
                        <TableCell align="right">{row.running ? '' : row.mappingCount}</TableCell>
                        <TableCell align="right">{row.running ? '' : `${(row.elapsedMs / 1000).toFixed(0)}s`}</TableCell>
                        <TableCell sx={{maxWidth: 320}}>
                            <Typography variant="caption" color="text.secondary" noWrap
                                title={row.error ?? row.integrityMessage ?? row.rejectionReason ?? ''}>
                                {row.error ?? row.integrityMessage ?? row.rejectionReason ?? ''}
                            </Typography>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );

    /** Groups resolution-log entries by category so repeats collapse into one accordion. */
    const categorizeReason = (reason: string): string => {
        if (reason.includes('does not exist')) {
            return 'Hallucinated nominations';
        }
        if (reason.includes('Observation call failed')) {
            return 'Observation call failures';
        }
        if (reason.includes('No precursor state in view')) {
            return 'Rejected by observation';
        }
        if (reason.includes('failed statement verification') || reason.includes('failed verification')) {
            return 'Rejected by verification';
        }
        return 'Other rejections';
    };

    const renderGroupedLog = (
        items: Array<{ reportId: string; text: string }>,
        categorize: (text: string) => string,
        helpByCategory?: Record<string, string>): ReactElement => {
        const categories = Array.from(new Set(items.map(item => categorize(item.text))));
        return (
            <Box>
                {categories.map((category: string): ReactElement => {
                    const group = items.filter(item => categorize(item.text) === category);
                    return (
                        <Accordion key={category} disableGutters defaultExpanded={group.length <= 3}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="body2" fontWeight="bold">
                                    {category} ({group.length})
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{pt: 0}}>
                                {helpByCategory?.[category] && (
                                    <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                                        {helpByCategory[category]}
                                    </Typography>
                                )}
                                {group.map((item, index) => (
                                    <Box key={index} sx={{borderTop: '1px solid', borderColor: 'divider', py: 0.75}}>
                                        <Typography variant="body2">
                                            <b>{item.reportId}</b>: {item.text}
                                        </Typography>
                                    </Box>
                                ))}
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Box>
        );
    };

    /** A control row's class, naming the unclassed case rather than showing NODE as if it meant something. */
    const describeControlClass = (nodeClass?: string): string =>
        !nodeClass || nodeClass === 'NODE' ? 'unclassed' : nodeClass;

    const renderStatTable = (head: string[], rows: Array<Array<string | number>>): ReactElement => (
        <Table size="small">
            <TableHead>
                <TableRow>
                    {head.map((label, index) => (
                        <TableCell key={label} align={index === 0 ? 'left' : 'right'}>{label}</TableCell>
                    ))}
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((cells, rowIndex) => (
                    <TableRow key={rowIndex}>
                        {cells.map((cell, cellIndex) => (
                            <TableCell key={cellIndex} align={cellIndex === 0 ? 'left' : 'right'}>{cell}</TableCell>
                        ))}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );

    const renderResult = (result: ShakedownResult): ReactElement => {
        const attentionCount = result.integrityFindings.length + result.silentGates.length;
        const logCount = result.rejections.length;
        const gateAudit = result.gateAudit ?? [];
        return (
            <Box>
                <Typography variant="body1" sx={{mb: 1}}>
                    {result.totals.reports} reports: <b>{result.totals.mapped} mapped</b>,{' '}
                    {result.totals.mappedIncomplete} flagged for review, {result.totals.notApplicable} not applicable
                    {result.totals.errors > 0 ? `, ${result.totals.errors} errors` : ''}
                    {' '}(took {(result.totals.wallElapsedMs / 60000).toFixed(1)} min,{' '}
                    {(result.totals.totalElapsedMs / 60000).toFixed(1)} min cumulative across parallel reports)
                </Typography>
                {gateAudit.length > 0 && (
                    <Alert severity="error" sx={{mb: 1}}>
                        <b>Gate arithmetic does not balance ({gateAudit.length} gate{gateAudit.length === 1 ? '' : 's'}).</b>{' '}
                        The per-node counts cannot be reconciled under the model&apos;s AND/OR logic, so these
                        results are not quantitatively trustworthy. See Model signals for the offending gates.
                    </Alert>
                )}
                <Tabs value={resultTab} onChange={(_, value: number): void => setResultTab(value)} sx={{mb: 1}}>
                    <Tab label="Model" />
                    <Tab label="Overview" />
                    <Tab label={`Needs attention (${attentionCount})`} />
                    <Tab label="Model signals" />
                    <Tab label={`Resolution log (${logCount})`} />
                    <Tab label={`Reports (${result.reports.length})`} />
                </Tabs>

                {resultTab === 0 && (
                    <Box sx={{height: 520}}>
                        <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                            The run drawn onto the model: what its reports did at each node. A branch with no
                            counts took no evidence from this report set, and a gate carrying more than its
                            children accounted for is marked as having reports that stopped there.
                        </Typography>
                        {model == null ? (
                            <Typography variant="body2" color="text.secondary">Loading the model…</Typography>
                        ) : (
                            <FaultTreeDendrogramView
                                nodeBadges={result.nodeBadges ?? []}
                                eventSequenceData={model}
                                getCollapsedNodeIds={(): Set<number> | null => collapsedNodeIdsRef.current}
                                setCollapsedNodeIds={(ids): void => { collapsedNodeIdsRef.current = ids; }}
                                getTransform={(): {x: number; y: number; k: number} | null => transformRef.current}
                                setTransform={(transform): void => { transformRef.current = transform; }}
                            />
                        )}
                    </Box>
                )}

                {resultTab === 1 && (
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                            Where the AI placed each report's entry point: the deepest state in the model that the
                            report demonstrates. A healthy spread matches your expectations for the report set;
                            heavy concentration on one broad state, or entries at states you would not expect,
                            point at statements that are too easy or too hard to satisfy.
                        </Typography>
                        <Typography variant="subtitle2" sx={{mb: 1}}>Where entries landed</Typography>
                        {renderStatTable(['Entry point', 'Reports'],
                            result.entryDistribution.map(entry => [entry.nodeName, entry.count]))}
                    </Box>
                )}

                {resultTab === 2 && (
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                            Reports that were mapped, but where the algorithm noticed something inconsistent and
                            flagged it for human review instead of guessing. The mapping was kept at the safest
                            supportable state; each note says exactly what disagreed.
                        </Typography>
                        {result.integrityFindings.length > 0 && (
                            <Box sx={{mb: 2}}>
                                <Typography variant="subtitle2" sx={{mb: 1}}>
                                    Integrity findings (mapped, but flagged for review)
                                </Typography>
                                {renderGroupedLog(
                                    result.integrityFindings.map(finding =>
                                        ({reportId: finding.reportId, text: finding.note})),
                                    (text: string): string => text.includes('did not stabilize')
                                        ? 'Resolution did not stabilize'
                                        : text.includes('Contradictory observations')
                                            ? 'Contradictory observations'
                                            : 'Verification disagreements',
                                    INTEGRITY_HELP)}
                            </Box>
                        )}
                        {result.silentGates.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2" sx={{mb: 1}}>
                                    Silent gates (report did not specify a sub-reason)
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                                    Reports confirmed these gates but never specified which child reason applied,
                                    so the branch ended there. This is not an error. Frequent hits on one gate
                                    suggest adding an &quot;other reason&quot; child, or simply that reports lack
                                    this level of detail.
                                </Typography>
                                {renderStatTable(['Gate', 'Times'],
                                    result.silentGates.map(gate => [gate.nodeName, gate.count]))}
                            </Box>
                        )}
                        {attentionCount === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{py: 3, textAlign: 'center'}}>
                                Nothing needs attention.
                            </Typography>
                        )}
                    </Box>
                )}

                {resultTab === 3 && (
                    <Box>
                        <Typography variant="subtitle2" sx={{mb: 1}}>Gate arithmetic audit</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                            Every AND gate must satisfy count(precursor child) = count(gate) + control
                            successes recorded at that gate. A control success is a barrier that held or a
                            circumstance that was absent, whether the report named the mechanism or the arrest
                            was implied because the parent state did not occur. A non-empty table means reports
                            left the arithmetic (silent stops) or interactions were attributed to gates that
                            were only walked through: the counts cannot sum under the model&apos;s boolean
                            logic. An empty table means the run balances.
                        </Typography>
                        {gateAudit.length === 0
                            ? (
                                <Typography variant="body2" sx={{mb: 3}} color="success.main">
                                    Balanced: every gate&apos;s counts reconcile.
                                </Typography>
                            )
                            : (
                                <Box sx={{mb: 3}}>
                                    {renderStatTable(['Gate', 'Expected', 'Actual', 'Delta'],
                                        gateAudit.map(g => [g.gateName, g.expected, g.actual, g.delta]))}
                                </Box>
                            )}
                        <Typography variant="subtitle2" sx={{mb: 1}}>Control judgments (decision window)</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                            How each control was judged across the run: held (it worked), failed, or not challenged
                            (the report never tested it). The mapping treats every non-precursor child of a gate as
                            a control. A row whose class is not BARRIER or CIRCUMSTANTIAL is an unclassed node being
                            judged as one: only a barrier can hold and only a circumstance can be present, so its
                            held and failed counts are the AI&apos;s reading of that node, not a safeguard working or
                            not. The Review tab flags such nodes. Degenerate rows are the signal: a control that
                            never holds may have a statement that reads as always failed; one that is never
                            challenged may be invisible to reports.
                        </Typography>
                        {renderStatTable(['Control', 'Class', 'Held', 'Failed', 'Not challenged'],
                            result.controlStats.map(stat =>
                                [stat.nodeName, describeControlClass(stat.nodeClass),
                                    stat.held, stat.failed, stat.notChallenged]))}
                        <Typography variant="subtitle2" sx={{mt: 3, mb: 1}}>Statement verification health</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                            Focused yes/no checks of individual statements of truth against the report text.
                            Statements that repeatedly fail verification are wordings the reports cannot satisfy
                            (for example demanding specific numeric criteria that narratives never state) and are
                            the first candidates for rewording.
                        </Typography>
                        {renderStatTable(['Statement (node)', 'Satisfied', 'Failed'],
                            result.verificationStats.map(stat =>
                                [stat.nodeName, stat.satisfied, stat.failed]))}
                    </Box>
                )}

                {resultTab === 4 && (
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                            The record of candidate entry points that were considered and rejected on the way to
                            each result. This is the algorithm defending itself, not a list of errors: rejections
                            cost little and usually mean the report did not fit the candidate.
                        </Typography>
                        {result.rejections.length > 0
                            ? renderGroupedLog(
                                result.rejections.map(rejection =>
                                    ({reportId: rejection.reportId, text: rejection.reason})),
                                categorizeReason,
                                REJECTION_HELP)
                            : (
                                <Typography variant="body2" color="text.secondary" sx={{py: 3, textAlign: 'center'}}>
                                    No candidate rejections.
                                </Typography>
                            )}
                    </Box>
                )}

                {resultTab === 5 && (
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                            One row per report. MAPPED: a verified entry point with its causal chain.
                            MAPPED_INCOMPLETE: mapped, but flagged for human review (see the note).
                            NOT_APPLICABLE: no entry point survived, which is the correct outcome for reports
                            outside this model's scope. Times include waiting for a free AI slot, so they overlap
                            heavily across parallel reports.
                        </Typography>
                        {renderRows(result.reports)}
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <Box>
            {phase === 'input' && renderInput()}
            {phase === 'running' && ((): ReactElement => {
                const rows = snapshot?.reports ?? [];
                const completed = rows.filter(row => !row.running).length;
                const total = snapshot?.reportsTotal ?? 0;
                return (
                    <Box>
                        <LinearProgress
                            variant={total > 0 ? 'determinate' : 'indeterminate'}
                            value={total > 0 ? (completed / total) * 100 : undefined}
                            sx={{mb: 1}}
                        />
                        <Typography variant="caption" color="text.secondary">
                            {snapshot?.statusMessage
                                ? snapshot.statusMessage
                                : total > 0
                                    ? `${completed} / ${total} reports mapped (${rows.length - completed} running)`
                                    : 'Preparing run...'}
                        </Typography>
                        <Box sx={{mt: 2}}>
                            {rows.length > 0 && renderRows(rows)}
                        </Box>
                    </Box>
                );
            })()}
            {phase === 'result' && snapshot?.result && renderResult(snapshot.result)}
            {phase === 'error' && (
                <Typography color="error" variant="body2">{errorMessage}</Typography>
            )}
            <Stack direction="row" justifyContent="flex-end" sx={{mt: 2}}>
                {phase === 'input' && (
                    <Button variant="contained" onClick={start} disabled={scope == null}>
                        Run Shakedown
                    </Button>
                )}
                {(phase === 'result' || phase === 'error') && (
                    <Button variant="outlined" onClick={(): void => {
                        if (cacheKey != null) {
                            delete getModelTestingCache(cacheKey).shakedown;
                        }
                        setSnapshot(null);
                        setErrorMessage(null);
                        setPhase('input');
                    }}>New Run</Button>
                )}
            </Stack>
        </Box>
    );
}
